import { forwardRef, type Ref, type ChangeEvent } from "react";
import { useFormFieldContext } from "../FormField/context";
import { type IInput, type InputProps } from "./types";
import {
  InputWrapper,
  StyledLabel,
  StyledInput,
  ErrorMessage,
  IconWrapper,
  Wrap,
  PrefixSlot,
  SuffixSlot,
  Field,
} from "./style";
import SearchIcon from "../../../assets/icons/SearchIcon";

/* =========================================================================
 * Legacy Input (default export) — preservado para compat com 8 consumidores.
 * Use o named export `Input` para código novo.
 * ========================================================================= */
const InputLegacy = forwardRef(
  (
    {
      theme,
      label,
      placeholder,
      defaultValue,
      name,
      onChange,
      required,
      type,
      variant,
      message,
      onKeyDownCapture,
      icon = false,
      register,
      mask,
      value,
    }: IInput & { mask?: (value: string) => string },
    ref: Ref<HTMLInputElement>,
  ) => {
    theme = theme || "modern";
    variant = variant || "unabled";

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      // Espalhar o elemento DOM (`{...e.target}`) devolve quase nada: name, type
      // e checked são accessors do prototype, não propriedades próprias. Quem lê
      // `e.target.name` no onChange recebia undefined e descartava a edição em
      // silêncio. Aplica a máscara no próprio input e repassa o evento original.
      if (mask) {
        e.target.value = mask(e.target.value);
      }
      // `{...register}` é espalhado ANTES deste handler no <input>, então o
      // onChange do react-hook-form era descartado: o campo registrado nunca
      // recebia valor. Os dois rodam, o do register primeiro (com a máscara já
      // aplicada), para que o form guarde o mesmo texto que o consumidor vê.
      void register?.onChange?.(e);
      onChange?.(e);
    };

    return (
      <InputWrapper $theme={theme} $variant={variant} $message={message}>
        {label && <StyledLabel>{label}</StyledLabel>}
        <div style={{ position: "relative" }}>
          {icon && (
            <IconWrapper>
              <SearchIcon />
            </IconWrapper>
          )}
          <StyledInput
            ref={ref}
            defaultValue={defaultValue}
            placeholder={placeholder}
            name={name}
            value={value}
            {...register}
            onChange={handleChange}
            onKeyDownCapture={onKeyDownCapture}
            required={required}
            type={type}
            $hasIcon={icon}
            autoComplete="off"
          />
        </div>
        {message && <ErrorMessage>{message}</ErrorMessage>}
      </InputWrapper>
    );
  },
);

InputLegacy.displayName = "InputLegacy";

export default InputLegacy;

/* =========================================================================
 * Novo Input primitivo (named export) — integra com FormField via context.
 * ========================================================================= */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { prefix, suffix, error: errorProp, size = "md", id: idProp, ...rest },
  ref,
) {
  const ctx = useFormFieldContext();
  const id = idProp ?? ctx?.id;
  const error = errorProp ?? !!ctx?.error;
  const ariaDescribedBy = rest["aria-describedby"] ?? ctx?.ariaDescribedBy;
  const ariaInvalid = rest["aria-invalid"] ?? ctx?.ariaInvalid;
  const ariaRequired = rest["aria-required"] ?? ctx?.required;

  return (
    <Wrap>
      {prefix ? <PrefixSlot $error={error}>{prefix}</PrefixSlot> : null}
      <Field
        ref={ref}
        id={id}
        $size={size}
        $hasPrefix={!!prefix}
        $hasSuffix={!!suffix}
        $error={error}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        {...rest}
      />
      {suffix ? <SuffixSlot $error={error}>{suffix}</SuffixSlot> : null}
    </Wrap>
  );
});

export type { InputProps, InputSize, IInput } from "./types";
